import { type CSSProperties, type FC, memo, useCallback, useEffect, useState } from "react";
import { useSetAtom } from "jotai";
import { cx } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Heading, Text } from "@stubhub/broadway-web";
import { CheckIcon, ChevronRightIcon, HistoryIcon, NotificationIcon } from "@stubhub/broadway-web/icons/functional";
import { AddFeature, Feature, RecordParticipationTypes, useSimpleFeatureTernary } from "@viagogo/feature";
import type { EventListGridItem } from "src/http/api-types";
import { Content, useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { NATIONAL_FOOTBALL_TEAMS } from "src/shared/constants/SportsCategories";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import { AlternativePerformerNewGrid } from "src/website-apps/responsive/stubhub-performer/components/alternative-performer-new-grid/AlternativePerformerNewGrid";
import { Calendar } from "src/website-apps/responsive/stubhub-performer/components/calendar/Calendar";
import { MobileCalendarFilterRow } from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/mobile-calendar-filter-row/MobileCalendarFilterRow";
import {
  FilterBar,
  ResidencyMobileEmptyState,
  ShowsAvailableCount,
  TitleSection,
} from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/residency";
import { FestivalCarouselModule } from "src/website-apps/responsive/stubhub-performer/components/festival-carousel/festival-carousel-module";
import { GAEventGridListItem } from "src/website-apps/responsive/stubhub-performer/components/ga-event-grid-list-item/ga-event-grid-list-item";
import { GAFilterSection } from "src/website-apps/responsive/stubhub-performer/components/ga-page/ga-filter-section";
import { useGaFilter } from "src/website-apps/responsive/stubhub-performer/components/ga-page/ga-filter-state";
import { GALocationSelector } from "src/website-apps/responsive/stubhub-performer/components/ga-page/ga-location-selector";
import { InfoBanners } from "src/website-apps/responsive/stubhub-performer/components/info-banners/InfoBanners";
import { ActionButtonsForMobileGlass } from "src/website-apps/responsive/stubhub-performer/components/page-body/action-buttons/ActionButtonsForMobileGlass";
import {
  bodyLayoutRecipe,
  bodyWrapperRecipe,
  buttonsContainer,
  calendarFiltersContainer,
  eventGridV2BackgroundDivRecipe,
  modernizedInfoCardSpacing,
  styledAboutButton,
  styledContainer,
} from "src/website-apps/responsive/stubhub-performer/components/page-body/body.styles";
import { PerformerCarouselSection } from "src/website-apps/responsive/stubhub-performer/components/page-body/carousel-section/PerformerCarouselSection";
import { FullWidthDivWrapper } from "src/website-apps/responsive/stubhub-performer/components/page-body/full-width-div-wrapper";
import { PerformerEventGridListItem } from "src/website-apps/responsive/stubhub-performer/components/performer-event-grid-list-item/performer-event-grid-list-item";
import { PerformerInfoCard } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card";
import { PerformerInfoCardMobileV2 } from "src/website-apps/responsive/stubhub-performer/components/PerformerInfoCardMobile/PerformerInfoCardMobileV2";
import { EventListFiltersStandalone } from "src/website-apps/responsive/stubhub-performer/components/portal/combined-header-banner/component-wrappers/EventListFiltersStandalone";
import {
  PortalColLeft,
  PortalColRight,
} from "src/website-apps/responsive/stubhub-performer/components/portal/PortalCol";
import { PerformerCarouselModule } from "src/website-apps/responsive/stubhub-performer/components/recently-viewed-events/PerformerCarouselModule";
import { RecentlyViewedEventItem } from "src/website-apps/responsive/stubhub-performer/components/recently-viewed-events/RecentlyViewedEventItem";
import { TrustCard } from "src/website-apps/responsive/stubhub-performer/components/trust-card/TrustCard";
import {
  hasTrustCardContent,
  useTrustCardContent,
} from "src/website-apps/responsive/stubhub-performer/components/trust-card/useTrustCardContent";
import { WelcomeBackBanner } from "src/website-apps/responsive/stubhub-performer/components/welcome-back-banner/WelcomeBackBanner";
import { useAltPerformerItems } from "src/website-apps/responsive/stubhub-performer/hooks/useAltPerformerItems";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { useScrollIntoView } from "src/website-apps/responsive/stubhub-performer/hooks/useScrollIntoView";
import { scrollToPerformerCardAtom } from "src/website-apps/responsive/stubhub-performer/utils/scrollToPerformerCardAtom";
import {
  AppInstallInFeedBanner,
  AppInstallInFeedBannerPlacements,
} from "src/website-apps/shared/components/app-install-in-feed-banner";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { FanProtectMessageBanner } from "src/website-apps/shared/components/fan-protect-message-banner/FanProtectMessageBanner";
import { FiltersAndGridsManager } from "src/website-apps/shared/components/FiltersAndGridsManager";
import { GuaranteeHighlights } from "src/website-apps/shared/components/guarantee-highlights/GuaranteeHighlights";
import { useGuaranteeHighlights } from "src/website-apps/shared/components/guarantee-highlights/useGuaranteeHighlights";
import { NotificationOptionEnum, useStoredNotifications } from "src/website-apps/shared/components/notifications";
import { NotificationModal } from "src/website-apps/shared/components/on-sale-notifications/NotificationModal";
import { PerformerSeasonTicketsAppPromo } from "src/website-apps/shared/components/season-tickets-app-promo";
import {
  canShowWorldCupFanProtectDisplay,
  isWorldCupFanProtectCategory,
  isWorldCupFanProtectExcludedCobrand,
} from "src/website-apps/shared/components/world-cup-fan-protect/eligibility";
import { WorldCupFanProtectDisplay } from "src/website-apps/shared/components/world-cup-fan-protect/WorldCupFanProtectDisplay";
import { PerformerViewMode } from "src/website-apps/shared/constants/performer-view-mode";
import { useDateCookieOptions } from "src/website-apps/shared/context/date-context";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useIsCalendarView } from "src/website-apps/shared/hooks/useIsCalendarView";
import { useLeague } from "src/website-apps/shared/hooks/useLeague";
import { usePerformerInfoCardData } from "src/website-apps/shared/hooks/usePerformerInfoCardData";
import { usePerformerViewMode } from "src/website-apps/shared/hooks/usePerformerViewMode";
import { getPerformerLeagueIdFromModel } from "src/website-apps/shared/utils/getPerformerLeagueIdFromModel";
import {
  getFestivalsGrid,
  getPerformerResidencyContext,
  hasPlayoffsSplitGridSurface,
  hasResidencyGridSurface,
  supportsFanProtectBanner,
  supportsTrustCard,
} from "src/website-apps/shared/utils/performerGridSurface";
import { FanProtectBanner } from "../fan-protect-banner/FanProtectBanner";
import { fanProtectMobilePlacement } from "../fan-protect-banner/styles";
import { PERFORMER_CARD_WIDTH_IN_PX } from "./constants";
import {
  fanProtectBannerContainer,
  PLAYOFF_ALERT_ONBOARDING_CSS_VARS,
  playoffAlertOnboardingRecipe,
  recentlyViewedCarouselContainer,
  trustCardContainer,
} from "./styles";

interface Props {
  /** Flag indicating if the user has visited the performer page ever before */
  userHasVisitedBefore: boolean;
  /** null = Google Sign-In didn't run; true = returning user; false = new user */
  isReturningGoogleUser: boolean | null;
}

/**
 * Layout body used for the performer page
 */
export const Body: FC<Props> = memo(({ userHasVisitedBefore, isReturningGoogleUser }) => {
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const model = usePerformerPageModelData();
  const isGaShoppingMode = model.performerGridSurface?.type === "ga-festival";
  const gaFilterCtx = useGaFilter();
  const guaranteeHighlights = useGuaranteeHighlights({ isEligibleForResale: isGaShoppingMode });
  const performerInfo = usePerformerInfoCardData();
  const recentlyViewedEvents = model.recentlyViewedEvents ?? [];
  /**
   * NOTE: if you run expriment on Body make sure this is on the outer wrapper so that "see events" in M performer card scrolls properly
   */
  const [scrollToTopOfEventGrids, topOfEventGridsRef] = useScrollIntoView<HTMLDivElement>(-30);

  const [scrollToMobilePerformerCard, topOfPerformerCardRef] = useScrollIntoView<HTMLDivElement>(-30);
  const setScrollTarget = useSetAtom(scrollToPerformerCardAtom);
  const handleAboutButtonClick = useCallback(() => {
    storePageEvent(
      PE_TYPE.CLICK,
      PE_KEYS.MOBILE_PERFORMER_CARD_ABOUT_BUTTON_CLICK,
      String(model.categorySummary.categoryName),
    );
    scrollToMobilePerformerCard();
  }, [scrollToMobilePerformerCard, model.categorySummary.categoryName, storePageEvent]);

  // Update the atom when the scroll function or ref changes.
  useEffect(() => {
    setScrollTarget({
      scrollToElement: handleAboutButtonClick,
      performerRef: topOfPerformerCardRef,
    });
  }, [handleAboutButtonClick, topOfPerformerCardRef, setScrollTarget]);

  const isTabletBreakpoint = useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const isMobile = !isTabletBreakpoint;
  const isTablet = isTabletBreakpoint && !isDesktop;

  const { isOnCalendarView, calendarRenderMode } = useIsCalendarView();
  const isWorldCupCategory = isWorldCupFanProtectCategory(model.categorySummary.categoryId);
  const performerViewMode = usePerformerViewMode();
  const isResidencyVariant = performerViewMode === PerformerViewMode.Residency;

  const [eventsGridState, eventsGridDispatch, { resetAllFiltersExcludingLocation }] = useEventsGridContext();
  const dateCookieOptions = useDateCookieOptions();
  const surface = eventsGridState.performerGridSurface ?? model.performerGridSurface;
  const festivalsGrid = getFestivalsGrid(surface);
  const trustCardContent = useTrustCardContent();
  const isResidencyEventListSurface = hasResidencyGridSurface(eventsGridState.performerGridSurface);
  const residencyEventCount = eventsGridState.grids?.reduce((sum, g) => sum + (g.totalCount ?? 0), 0) ?? 0;
  const isResidencyGridUpdating = eventsGridState.grids?.some((g) => g.isUpdating) ?? false;
  const handleResidencyResetFilters = useCallback(() => {
    resetAllFiltersExcludingLocation(eventsGridDispatch, eventsGridState, false, dateCookieOptions);
  }, [dateCookieOptions, resetAllFiltersExcludingLocation, eventsGridDispatch, eventsGridState]);

  const isDesktopCalendarViewVisible = isOnCalendarView && isDesktop;

  const isPerformerCardInBody = !isMobile && !isOnCalendarView;

  const PortalMainContentWrapper = isPerformerCardInBody ? PortalColLeft : FullWidthDivWrapper;

  const showNewMBannerCard = isMobile || isTablet;

  const showMobileGlassHeader = !isDesktop && !showNewMBannerCard;
  const isStubhub = useIsStubhub();
  const hasAnyEvents = (eventsGridState.grids?.reduce((sum, g) => sum + (g.totalCount ?? 0), 0) ?? 0) > 0;
  const leagueId = getPerformerLeagueIdFromModel(model);
  const { isNFL, isNCAAFB } = useLeague(leagueId);
  const canShowNgsSeasonTicketsPromo = (isNFL || isNCAAFB) && isDesktop && isStubhub;
  const showDesktopTrustBanner = isDesktop;
  const isWorldCupRelatedCategory =
    isWorldCupCategory ||
    (!!model.categorySummary.categoryId && NATIONAL_FOOTBALL_TEAMS.includes(model.categorySummary.categoryId));
  const isFanProtectExcludedCobrand = isWorldCupFanProtectExcludedCobrand(appContext.cobrandId);
  const canShowDesktopTrustBanner = showDesktopTrustBanner && isWorldCupRelatedCategory && !isFanProtectExcludedCobrand;
  const canShowWorldCupFanProtect =
    showDesktopTrustBanner && canShowWorldCupFanProtectDisplay(model.categorySummary.categoryId, appContext.cobrandId);

  const residencyContext = getPerformerResidencyContext(model.performerGridSurface);
  const venueLocation =
    residencyContext?.venueLatitude !== null &&
    residencyContext?.venueLatitude !== undefined &&
    residencyContext.venueLongitude !== null &&
    residencyContext.venueLongitude !== undefined
      ? { lt: residencyContext.venueLatitude, lg: residencyContext.venueLongitude }
      : undefined;
  const altPerformerItems = useAltPerformerItems(venueLocation);

  const isOnPlayoffsPerformerPage = hasPlayoffsSplitGridSurface(eventsGridState.performerGridSurface);
  const showPlayoffNotificationOnboarding = useSimpleFeatureTernary(
    FeatureSetEnum.TMPerformerPlayoffNotifications,
    false,
    true,
    () => isOnPlayoffsPerformerPage,
  );
  // record: false — the modernized-card participation is recorded by the event cards; this is a
  // read-only check so the page can add variant-only spacing without a second recording site.
  const modernizedCardActive = useSimpleFeatureTernary(
    FeatureSetEnum.MPerformerModernizedEventCard,
    false,
    true,
    undefined,
    false,
  );
  // Spacing pairs with the modernized card — calendar view and GA shopping mode swap that card out
  // and record no participation, so the spacing must not apply there either.
  const showModernizedInfoCardSpacing =
    modernizedCardActive && !isGaShoppingMode && !(isOnCalendarView && !isResidencyEventListSurface);
  const playoffNotificationTitle = useContent("cid$#4220844#"); // "Follow the action"
  const playoffNotificationSubtitle = useContent("cid$#4220845#"); // "Get alerts for schedule updates, matchups, and ticket releases."
  const playoffNotificationOnboardedHeading = useContent("cid$#4220847#"); // "You're all set for alerts"
  const playoffNotificationConfirmationContent = useContent("cid$#4220848#"); // "We've got you covered. As soon as the game details are officially confirmed, you'll be the first to know."
  const playoffAlertOnboardingStyles = playoffAlertOnboardingRecipe();

  const storedNotifications = useStoredNotifications();
  const isPlayoffOnboarded =
    model.categorySummary.categoryId !== undefined &&
    storedNotifications.isNotificationStored(model.categorySummary.categoryId, NotificationOptionEnum.PERFORMER);

  const [isPlayoffNotificationModalOpen, setIsPlayoffNotificationModalOpen] = useState(false);
  const handlePlayoffNotificationOpen = useCallback(() => {
    storePageEvent(
      PE_TYPE.CLICK,
      PE_KEYS.PLAYOFF_NOTIFICATION_ONBOARDING_CLICK,
      String(model.categorySummary.categoryId ?? ""),
    );
    setIsPlayoffNotificationModalOpen(true);
  }, [model.categorySummary.categoryId, storePageEvent]);
  const handlePlayoffNotificationClose = useCallback(() => {
    setIsPlayoffNotificationModalOpen(false);
  }, []);

  const isRelative = isStubhub && (appContext.isUserLoggedIn || userHasVisitedBefore);
  const containerFullWidth = isOnCalendarView && !isMobile;
  const layoutKey = containerFullWidth ? "fluid" : "default";
  const computedLayoutKey = isResidencyVariant ? "residency" : layoutKey;

  const layout = bodyLayoutRecipe({ layout: computedLayoutKey });
  return (
    <>
      {!isDesktop && !showNewMBannerCard ? (
        <div className={eventGridV2BackgroundDivRecipe({ positioning: isRelative ? "relative" : "static" })}>
          {showMobileGlassHeader &&
            (!isStubhub && userHasVisitedBefore ? (
              <WelcomeBackBanner />
            ) : (
              appContext.isUserLoggedIn && isReturningGoogleUser !== false && <WelcomeBackBanner />
            ))}
          <div>
            {showMobileGlassHeader && (
              <div className={buttonsContainer}>
                <button type="button" className={styledAboutButton} onClick={handleAboutButtonClick}>
                  {/* "About" */}
                  <Content id="cid$#3438357#" />
                </button>
                <ActionButtonsForMobileGlass
                  categoryName={model.categorySummary.categoryName ?? ""}
                  categoryId={model.categorySummary.categoryId}
                  aggregateFavorites={model.categorySummary.aggregateFavorites}
                />
              </div>
            )}
            {!isResidencyVariant && (
              <Feature
                id={FeatureSetEnum.TMPerformerDemandTrendBanner}
                control={() => null}
                variant={({ children }) => children}
              >
                {!isGaShoppingMode && <InfoBanners />}
              </Feature>
            )}
          </div>
        </div>
      ) : null}
      <div
        className={bodyWrapperRecipe({
          surface: isOnCalendarView ? "none" : "white",
          shoppingMode: isGaShoppingMode ? "ga" : "standard",
        })}
      >
        {!isDesktop && recentlyViewedEvents.length > 0 && (
          <div className={recentlyViewedCarouselContainer}>
            <PerformerCarouselModule<EventListGridItem>
              items={recentlyViewedEvents}
              getItemKey={(e) => String(e.eventId)}
              carouselId={CarouselId.RecentlyViewed}
              itemType={CarouselItemType.Event}
              renderItem={(event, i, onItemClick) => (
                <RecentlyViewedEventItem
                  event={event}
                  position={i}
                  onItemClick={onItemClick}
                  compactSubtitle={isMobile || recentlyViewedEvents.length > 1}
                />
              )}
              header={
                <>
                  <HistoryIcon size={16} />
                  <Text variant="label" size="extra-small">
                    {/* "Recently viewed" */}
                    <Content id="cid$#3528512#" />
                  </Text>
                </>
              }
            />
          </div>
        )}
        {!!showNewMBannerCard && !isResidencyVariant && !isGaShoppingMode && <InfoBanners noTopSpacing />}
        <div className={layout.container}>
          <div ref={topOfEventGridsRef} className={layout.row}>
            <div className={layout.col}>
              {isDesktop ? (
                <>
                  <PortalMainContentWrapper setMinHeight flex="1 1 auto">
                    {!isResidencyVariant && !isGaShoppingMode && (
                      <InfoBanners noTopSpacing={!isDesktopCalendarViewVisible} />
                    )}
                    {isDesktopCalendarViewVisible ? (
                      <>
                        {calendarRenderMode !== PerformerViewMode.Residency && (
                          <div className={calendarFiltersContainer}>
                            <EventListFiltersStandalone filterTypeOptions={model.gridFilterTypes ?? []} />
                          </div>
                        )}
                        <Calendar renderMode={calendarRenderMode} />
                      </>
                    ) : (
                      <>
                        {recentlyViewedEvents.length > 0 && (
                          <div className={recentlyViewedCarouselContainer}>
                            <PerformerCarouselModule<EventListGridItem>
                              items={recentlyViewedEvents}
                              getItemKey={(e) => String(e.eventId)}
                              carouselId={CarouselId.RecentlyViewed}
                              itemType={CarouselItemType.Event}
                              renderItem={(event, i, onItemClick) => (
                                <RecentlyViewedEventItem
                                  event={event}
                                  position={i}
                                  onItemClick={onItemClick}
                                  compactSubtitle={isMobile || recentlyViewedEvents.length > 1}
                                />
                              )}
                              header={
                                <>
                                  <HistoryIcon size={16} />
                                  <Text variant="label" size="extra-small">
                                    {/* "Recently viewed" */}
                                    <Content id="cid$#3528512#" />
                                  </Text>
                                </>
                              }
                            />
                          </div>
                        )}
                        {isGaShoppingMode && (
                          <>
                            <GALocationSelector />
                            <InfoBanners noTopSpacing />
                            <GAFilterSection />
                          </>
                        )}
                        <FiltersAndGridsManager
                          filterTypeOptions={model.gridFilterTypes ?? []}
                          categoryName={model.categorySummary.categoryName ?? ""}
                          categoryId={model.categorySummary.categoryId ?? -1}
                          hideFilters={isGaShoppingMode}
                          hideGridTitle={isGaShoppingMode}
                          EventGridListItemComponent={
                            isGaShoppingMode ? GAEventGridListItem : PerformerEventGridListItem
                          }
                          AltPerformerGrid={AlternativePerformerNewGrid}
                          altPerformerItems={altPerformerItems}
                          gaFilterItems={gaFilterCtx?.filterItems}
                        />
                        {festivalsGrid.length > 0 && (
                          <AddFeature
                            id={FeatureSetEnum.TMPerformerFestivalEventCarousel}
                            recordType={RecordParticipationTypes.IN_VIEW}
                            canParticipate={() => festivalsGrid.length > 0}
                            feature={() => (
                              <FestivalCarouselModule
                                festivals={festivalsGrid}
                                performerName={model.categorySummary.categoryName ?? ""}
                              />
                            )}
                          />
                        )}
                      </>
                    )}
                  </PortalMainContentWrapper>
                  {isPerformerCardInBody ? (
                    <PortalColRight
                      layout="stacked"
                      minWidth={PERFORMER_CARD_WIDTH_IN_PX}
                      flex={`0 0 ${String(PERFORMER_CARD_WIDTH_IN_PX)}px`}
                    >
                      <div
                        ref={showNewMBannerCard ? topOfPerformerCardRef : undefined}
                        style={{ marginBlockStart: "-100px" }}
                      >
                        <PerformerInfoCard />
                      </div>
                      <AddFeature
                        id={FeatureSetEnum.TPerformerTrustCard}
                        recordType={RecordParticipationTypes.IN_VIEW}
                        canParticipate={() => supportsTrustCard(surface) && hasTrustCardContent(trustCardContent)}
                        feature={() => (
                          <div className={trustCardContainer}>
                            <TrustCard />
                          </div>
                        )}
                      />
                      {isGaShoppingMode && (
                        <div>
                          <GuaranteeHighlights highlights={guaranteeHighlights} />
                        </div>
                      )}
                      {showPlayoffNotificationOnboarding && (
                        <button
                          type="button"
                          className={playoffAlertOnboardingStyles.button}
                          onClick={handlePlayoffNotificationOpen}
                          style={
                            {
                              [PLAYOFF_ALERT_ONBOARDING_CSS_VARS.primaryColor]:
                                model.categoryMetadata?.sports?.partnerTheme?.primaryColor,
                            } as CSSProperties
                          }
                        >
                          <div className={playoffAlertOnboardingStyles.ctaContainer}>
                            <NotificationIcon />
                          </div>
                          <div className={playoffAlertOnboardingStyles.bodyContainer}>
                            {isPlayoffOnboarded ? (
                              <Heading variant="title" size="medium" color="text">
                                {playoffNotificationOnboardedHeading}
                              </Heading>
                            ) : (
                              <>
                                <Heading variant="title" size="medium" color="text">
                                  {playoffNotificationTitle}
                                </Heading>
                                <Text variant="body" size="small" color="text.subtle">
                                  {playoffNotificationSubtitle}
                                </Text>
                              </>
                            )}
                          </div>
                          {isPlayoffOnboarded ? (
                            <CheckIcon classes={{ root: cx(playoffAlertOnboardingStyles.endIcon) }} />
                          ) : (
                            <ChevronRightIcon classes={{ root: cx(playoffAlertOnboardingStyles.endIcon) }} />
                          )}
                        </button>
                      )}
                      {canShowDesktopTrustBanner && (
                        <Feature
                          id={FeatureSetEnum.TMWorldCupFanProtectDisplay}
                          recordType={RecordParticipationTypes.IN_VIEW}
                          canParticipate={() => canShowWorldCupFanProtect}
                          control={() => (
                            <div className={fanProtectBannerContainer}>
                              <FanProtectMessageBanner useDarkerIconBg />
                            </div>
                          )}
                          variant={() => (
                            <WorldCupFanProtectDisplay mode="desktop" className={fanProtectBannerContainer} />
                          )}
                        />
                      )}
                      <AddFeature
                        id={FeatureSetEnum.TNgsMarketingForNflNcaaf}
                        recordType={RecordParticipationTypes.IN_VIEW}
                        canParticipate={() => canShowNgsSeasonTicketsPromo}
                        feature={() => (
                          <PerformerSeasonTicketsAppPromo
                            leagueType={isNFL ? "nfl" : "ncaaf"}
                            categoryId={model.categorySummary.categoryId}
                          />
                        )}
                      />
                    </PortalColRight>
                  ) : null}
                </>
              ) : (
                <>
                  {isOnCalendarView && !isDesktop && !isResidencyEventListSurface ? (
                    <>
                      <MobileCalendarFilterRow />
                      <Calendar renderMode={calendarRenderMode} />
                      {!appContext.isNativeApp && (
                        <AddFeature
                          id={FeatureSetEnum.MPerformerAppInstallInFeedBanner}
                          recordType={RecordParticipationTypes.IN_VIEW}
                          feature={() => (
                            <AppInstallInFeedBanner
                              hasEvents={hasAnyEvents}
                              placement={AppInstallInFeedBannerPlacements.PERFORMER}
                            />
                          )}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {isResidencyEventListSurface && (
                        <>
                          {isMobile && <TitleSection />}
                          <FilterBar filterTypeOptions={model.gridFilterTypes ?? []} />
                          {!isResidencyGridUpdating && residencyEventCount === 0 ? (
                            <ResidencyMobileEmptyState resetFilters={handleResidencyResetFilters} />
                          ) : (
                            <ShowsAvailableCount />
                          )}
                        </>
                      )}
                      {showPlayoffNotificationOnboarding && (
                        <button
                          type="button"
                          className={playoffAlertOnboardingStyles.button}
                          onClick={handlePlayoffNotificationOpen}
                          style={
                            {
                              [PLAYOFF_ALERT_ONBOARDING_CSS_VARS.primaryColor]:
                                model.categoryMetadata?.sports?.partnerTheme?.primaryColor,
                            } as CSSProperties
                          }
                        >
                          <div className={playoffAlertOnboardingStyles.bodyContainer}>
                            {isPlayoffOnboarded ? (
                              <Heading variant="title" size="medium" color="text">
                                {playoffNotificationOnboardedHeading}
                              </Heading>
                            ) : (
                              <>
                                <Heading variant="title" size="medium" color="text">
                                  {playoffNotificationTitle}
                                </Heading>
                                <Text variant="body" size="small" color="text.subtle">
                                  {playoffNotificationSubtitle}
                                </Text>
                              </>
                            )}
                          </div>
                          {isPlayoffOnboarded ? (
                            <CheckIcon classes={{ root: cx(playoffAlertOnboardingStyles.endIcon) }} />
                          ) : (
                            <div className={playoffAlertOnboardingStyles.ctaContainer}>
                              <NotificationIcon />
                            </div>
                          )}
                        </button>
                      )}
                      {isGaShoppingMode && (
                        <>
                          <GALocationSelector />
                          <InfoBanners noTopSpacing />
                          <GAFilterSection />
                        </>
                      )}
                      <FiltersAndGridsManager
                        filterTypeOptions={model.gridFilterTypes ?? []}
                        categoryName={model.categorySummary.categoryName ?? ""}
                        categoryId={model.categorySummary.categoryId}
                        hideGridTitle={isResidencyEventListSurface || isGaShoppingMode}
                        hideFilters={isResidencyEventListSurface || isGaShoppingMode}
                        forceShowEventGrids={isResidencyEventListSurface}
                        EventGridListItemComponent={isGaShoppingMode ? GAEventGridListItem : PerformerEventGridListItem}
                        AltPerformerGrid={AlternativePerformerNewGrid}
                        altPerformerItems={altPerformerItems}
                        gaFilterItems={gaFilterCtx?.filterItems}
                      />
                      {festivalsGrid.length > 0 && (
                        <AddFeature
                          id={FeatureSetEnum.TMPerformerFestivalEventCarousel}
                          recordType={RecordParticipationTypes.IN_VIEW}
                          canParticipate={() => festivalsGrid.length > 0}
                          feature={() => (
                            <FestivalCarouselModule
                              festivals={festivalsGrid}
                              performerName={model.categorySummary.categoryName ?? ""}
                            />
                          )}
                        />
                      )}
                      {!appContext.isNativeApp && (
                        <AddFeature
                          id={FeatureSetEnum.MPerformerAppInstallInFeedBanner}
                          recordType={RecordParticipationTypes.IN_VIEW}
                          feature={() => (
                            <AppInstallInFeedBanner
                              hasEvents={hasAnyEvents}
                              placement={AppInstallInFeedBannerPlacements.PERFORMER}
                            />
                          )}
                        />
                      )}
                    </>
                  )}
                  {showNewMBannerCard &&
                    (showModernizedInfoCardSpacing ? (
                      <div className={modernizedInfoCardSpacing}>
                        <PerformerInfoCardMobileV2
                          ref={topOfPerformerCardRef}
                          onClickSeeEvents={scrollToTopOfEventGrids}
                        />
                      </div>
                    ) : (
                      <PerformerInfoCardMobileV2
                        ref={topOfPerformerCardRef}
                        onClickSeeEvents={scrollToTopOfEventGrids}
                      />
                    ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <AddFeature
        id={FeatureSetEnum.MPerformerFanProtectBannerV2}
        recordType={RecordParticipationTypes.IN_VIEW}
        canParticipate={() => isMobile && supportsFanProtectBanner(surface)}
        feature={() => (
          <div className={fanProtectMobilePlacement}>
            <FanProtectBanner />
          </div>
        )}
      />
      <div className={styledContainer}>
        <PerformerCarouselSection performerInfo={performerInfo} />
      </div>
      {showPlayoffNotificationOnboarding && model.categorySummary.categoryId !== undefined && (
        <NotificationModal
          isOpen={isPlayoffNotificationModalOpen}
          onClose={handlePlayoffNotificationClose}
          userFirstName={appContext.userFirstName ?? ""}
          userEmailAddress={appContext.userEmailAddress ?? ""}
          performerName={model.categorySummary.categoryName ?? ""}
          entityId={model.categorySummary.categoryId}
          notificationType={NotificationOptionEnum.PERFORMER}
          notificationModalContent={{
            hasValue: true,
            notificationModalFormTitle: playoffNotificationTitle,
            notificationModalFormContent: playoffNotificationSubtitle,
            notificationModalConfirmationContent: playoffNotificationConfirmationContent,
          }}
        />
      )}
    </>
  );
});
Body.displayName = "Body";
